/**
 * Grid Control — Edit Modal
 *
 * Two modes:
 *   1. Built-in modal: renders a simple field-per-column form (no editFormID).
 *   2. Form modal:    loads a FormSchema by editFormID and renders it via FormRenderer.
 *
 * SRP: this file is responsible ONLY for the modal UI and form-fetch logic.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { FormRenderer } from '../../../components/FormRenderer';
import { FormDefinitionApiClient, getAtlasApiConfig, getCsrfToken, getCsrfTokenHeaderName } from '@atlas-forms/client-js';
import { fetchAuthToken } from '@passport/auth-session';
import type { FormSchema } from '@atlas-forms/types-js';
import type { GridColumn, GridRow } from './grid.types';
import { getCellValue, setCellValue, getDeep } from './grid.helpers';

// ─── Props ────────────────────────────────────────────────────────────────────

interface GridEditModalProps {
  row: GridRow;
  columns: GridColumn[];
  editFormID?: number | null;
  /**
   * Operating mode for the `editFormID`-backed form path only (the built-in
   * column-per-field modal below is always editable — it has no view mode).
   * 'view' renders the target form read-only (FormRenderer's `mode="view"`) with a
   * single Close action; 'edit' (default) renders it fully interactive with a real
   * Save action. Lets one `editFormID` serve both a grid's "View" and "Edit" row
   * actions without needing two separate form schemas.
   */
  formMode?: 'view' | 'edit';
  /**
   * When set, Save PUTs (or POSTs, per `saveMethod`) the edited fields to this real endpoint
   * before calling `onSave` — see the `saveEndpointTemplate` doc comment on grid.types.ts's
   * `LinkActionLeaf` 'form' case for why this matters and stays optional/additive.
   */
  saveEndpoint?: string;
  saveMethod?: 'PUT' | 'POST';
  /** See `dataRootPath` doc comment on grid.types.ts's `LinkActionLeaf` 'form' case. */
  dataRootPath?: string;
  onSave: (updated: GridRow) => void;
  onCancel: () => void;
}

// ─── Built-in field renderer ──────────────────────────────────────────────────

interface BuiltinFieldProps {
  col: GridColumn;
  value: unknown;
  onChange: (val: unknown) => void;
}

const BuiltinField: React.FC<BuiltinFieldProps> = ({ col, value, onChange }) => {
  const type = col.type ?? 'text';

  if (type === 'computed' || type === 'selector' || type === 'link') return null;

  const strVal = String(value ?? '');

  switch (type) {
    case 'select':
      return (
        <div className="af-grid-modal__field">
          <label className="af-grid-modal__label">
            {col.label}
            {col.required && <span className="af-grid-modal__required"> *</span>}
          </label>
          <select
            className="af-grid-modal__input"
            value={strVal}
            onChange={(e) => onChange(e.target.value)}
          >
            <option value="">— select —</option>
            {(col.options ?? []).map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      );

    case 'toggle':
      return (
        <div className="af-grid-modal__field af-grid-modal__field--toggle">
          <label className="af-grid-modal__label">
            <input
              type="checkbox"
              checked={Boolean(value)}
              onChange={(e) => onChange(e.target.checked)}
            />
            {' '}
            {col.label}
          </label>
        </div>
      );

    case 'number':
      return (
        <div className="af-grid-modal__field">
          <label className="af-grid-modal__label">
            {col.label}
            {col.required && <span className="af-grid-modal__required"> *</span>}
          </label>
          <input
            type="number"
            className="af-grid-modal__input"
            value={strVal}
            min={col.min}
            max={col.max}
            step={col.step}
            placeholder={col.placeholder}
            onChange={(e) => onChange(e.target.valueAsNumber)}
          />
        </div>
      );

    case 'date':
      return (
        <div className="af-grid-modal__field">
          <label className="af-grid-modal__label">
            {col.label}
            {col.required && <span className="af-grid-modal__required"> *</span>}
          </label>
          <input
            type="date"
            className="af-grid-modal__input"
            value={strVal}
            onChange={(e) => onChange(e.target.value)}
          />
        </div>
      );

    case 'css-class':
      return (
        <div className="af-grid-modal__field">
          <label className="af-grid-modal__label">
            {col.label}
            {col.required && <span className="af-grid-modal__required"> *</span>}
          </label>
          <input
            type="text"
            className="af-grid-modal__input"
            value={strVal}
            placeholder={col.placeholder ?? 'CSS class name'}
            onChange={(e) => onChange(e.target.value)}
          />
        </div>
      );

    default: // text
      return (
        <div className="af-grid-modal__field">
          <label className="af-grid-modal__label">
            {col.label}
            {col.required && <span className="af-grid-modal__required"> *</span>}
          </label>
          <input
            type="text"
            className="af-grid-modal__input"
            value={strVal}
            placeholder={col.placeholder}
            onChange={(e) => onChange(e.target.value)}
          />
        </div>
      );
  }
};

// ─── Built-in modal body ──────────────────────────────────────────────────────

interface BuiltinModalBodyProps {
  row: GridRow;
  columns: GridColumn[];
  onSave: (updated: GridRow) => void;
  onCancel: () => void;
}

const BuiltinModalBody: React.FC<BuiltinModalBodyProps> = ({ row, columns, onSave, onCancel }) => {
  const [draft, setDraft] = useState<GridRow>(row);

  const handleChange = useCallback(
    (col: GridColumn, val: unknown) => {
      setDraft((prev) => setCellValue(prev, col, val) as GridRow);
    },
    [],
  );

  // Guard against null — JSON config may set columns: null rather than omitting the key
  const safeColumns = columns ?? [];
  const editableColumns = safeColumns.filter(
    (c) => c.key && c.type !== 'computed' && c.type !== 'selector' && c.type !== 'link' && !c.key.startsWith('_'),
  );

  return (
    <>
      <div className="af-grid-modal__body">
        {editableColumns.map((col) => (
          <BuiltinField
            key={col.key}
            col={col}
            value={getCellValue(draft, col)}
            onChange={(val) => handleChange(col, val)}
          />
        ))}
      </div>
      <div className="af-grid-modal__footer">
        <button className="af-grid-modal__btn af-grid-modal__btn--secondary" onClick={onCancel}>
          Cancel
        </button>
        <button className="af-grid-modal__btn af-grid-modal__btn--primary" onClick={() => onSave(draft)}>
          Save
        </button>
      </div>
    </>
  );
};

// Module-level schema cache — intentionally shared across all grid instances on the page.
// Avoids redundant network requests when multiple grids reference the same form ID.
// Call clearFormSchemaCache() if a form definition has been updated server-side.
const _formSchemaCache = new Map<number, FormSchema>();

export const clearFormSchemaCache = (formId?: number): void => {
  if (formId !== undefined) {
    _formSchemaCache.delete(formId);
  } else {
    _formSchemaCache.clear();
  }
};

// ─── Form-backed modal body ───────────────────────────────────────────────────

interface FormModalBodyProps {
  row: GridRow;
  editFormID: number;
  mode: 'view' | 'edit';
  saveEndpoint?: string;
  saveMethod?: 'PUT' | 'POST';
  dataRootPath?: string;
  onSave: (updated: GridRow) => void;
  onCancel: () => void;
}

const FormModalBody: React.FC<FormModalBodyProps> = ({
  row,
  editFormID,
  mode,
  saveEndpoint,
  saveMethod,
  dataRootPath,
  onSave,
  onCancel,
}) => {
  const [schema, setSchema] = useState<FormSchema | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Real save in flight (only relevant when `saveEndpoint` is set) — disables the Save button
  // and swaps its label, same UX convention LinkCell's own `isDeleting` already established for
  // this file's sibling async action (the delete link).
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  // Tracks the form's current field values as the user edits — FormRenderer is rendered
  // with `nested` here (this modal already supplies its own dialog chrome, and GridControl
  // itself commonly lives inside an outer <form>, so a second nested <form> would be invalid
  // HTML), which means FormRenderer suppresses its own Save/Cancel footer. This modal supplies
  // its own footer below instead, and reads the latest values from here on Save.
  const [draftValues, setDraftValues] = useState<Record<string, unknown>>({});

  // ROOT-CAUSE FIX (2026-09-03) — "edit screen automatically reverts changes": this was
  // previously a plain object literal, rebuilt from `row` on every render of this component.
  // `onValuesChange={setDraftValues}` below fires on every keystroke, which re-renders this
  // component, which used to rebuild `initialValues` as a brand-new object reference from the
  // ORIGINAL (never-updated) `row` prop -- and FormRenderer's own schema/initialValues sync
  // effect resets its internal engine state back to whatever `initialValues` currently holds
  // any time that reference changes. The result: type a character -> onValuesChange fires ->
  // this re-renders -> a fresh (still-original-data) initialValues object is passed down ->
  // FormRenderer resets the field back to the pre-edit value -- the exact same object-identity
  // feedback loop as the search-results grid bug (AtlasFormsStudioApp.tsx's handleFieldChange),
  // just a second, independent instance of it in this file. Fixed with a lazy `useState`
  // initializer: computed once from `row` when the modal first mounts for this row, and never
  // recomputed on subsequent re-renders (including the ones `onValuesChange` itself causes),
  // so the reference FormRenderer sees stays stable for the lifetime of the edit session.
  const [initialValues] = useState<Record<string, unknown>>(() => {
    // dataRootPath: the target form's controls are flat field IDs, but this row's real data
    // lives nested under e.g. "fields" (the generic entity API's response shape) rather than
    // at the row's own top level (the Documents API's flat shape, dataRootPath unset) — read
    // through it so the form's fields actually populate instead of rendering blank. See this
    // prop's doc comment on grid.types.ts's `LinkActionLeaf` 'form' case.
    if (dataRootPath) {
      const nested = getDeep(row, dataRootPath);
      return nested && typeof nested === 'object' ? { ...(nested as Record<string, unknown>) } : {};
    }
    const iv: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(row)) {
      if (!k.startsWith('_')) iv[k] = v;
    }
    return iv;
  });

  useEffect(() => {
    let cancelled = false;

    const cached = _formSchemaCache.get(editFormID);
    if (cached) {
      setSchema(cached);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const client = new FormDefinitionApiClient({ baseUrl: getAtlasApiConfig().baseUrl });
    client
      .getFormById(editFormID)
      .then((res) => {
        if (cancelled) return;
        if (!res.success || !res.data) {
          setError('Form not found');
          return;
        }
        // Schema may be stored as a JSON string or already parsed
        const raw: unknown = res.data.Schema;
        const parsed: FormSchema =
          typeof raw === 'string' ? (JSON.parse(raw) as FormSchema) : (raw as unknown as FormSchema);
        _formSchemaCache.set(editFormID, parsed);
        setSchema(parsed);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load form');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [editFormID]);

  // Merges edited flat field values back into the row shape `onSave` expects — under
  // `dataRootPath` (e.g. row.fields = {...row.fields, ...values}) when set, otherwise spread
  // at the row's own top level, matching this component's pre-existing flat-row behavior.
  const mergeIntoRow = useCallback(
    (values: Record<string, unknown>): GridRow => {
      if (dataRootPath) {
        const currentNested = getDeep(row, dataRootPath);
        const nextNested = {
          ...(currentNested && typeof currentNested === 'object' ? (currentNested as Record<string, unknown>) : {}),
          ...values,
        };
        return { ...row, [dataRootPath]: nextNested } as GridRow;
      }
      return { ...row, ...values } as GridRow;
    },
    [row, dataRootPath],
  );

  const performSave = useCallback(
    async (values: Record<string, unknown>) => {
      if (!saveEndpoint) {
        onSave(mergeIntoRow(values));
        return;
      }

      setSaving(true);
      setSaveError(null);
      try {
        const { baseUrl } = getAtlasApiConfig();
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        try {
          const session = fetchAuthToken();
          if (session?.token) headers.Authorization = `Bearer ${session.token}`;
        } catch (err) {
          console.warn('[GridEditModal] fetchAuthToken() failed; saving without an Authorization header:', err);
        }
        try {
          const csrfToken = await getCsrfToken();
          if (csrfToken) headers[getCsrfTokenHeaderName()] = csrfToken;
        } catch (err) {
          console.warn('[GridEditModal] getCsrfToken() failed; saving without a CSRF header:', err);
        }

        const res = await fetch(`${baseUrl}${saveEndpoint}`, {
          method: saveMethod ?? 'PUT',
          headers,
          credentials: 'include',
          // Matches this API family's UpdateWebRequest shape ({ data: {...} }) — same
          // convention BaseEntityController.Update and every other Go entity's PUT expects.
          body: JSON.stringify({ data: values }),
        });
        if (!res.ok) {
          throw new Error(`Save failed: HTTP ${res.status}`);
        }
        onSave(mergeIntoRow(values));
      } catch (err) {
        console.error('[GridEditModal] Form Save action failed:', err);
        setSaveError(err instanceof Error ? err.message : 'Save failed');
      } finally {
        setSaving(false);
      }
    },
    [saveEndpoint, saveMethod, mergeIntoRow, onSave],
  );

  const handleSubmit = useCallback(
    (values: Record<string, unknown>) => {
      void performSave(values);
    },
    [performSave],
  );

  // Footer "Save" (edit mode only) uses the latest tracked draft rather than relying on
  // FormRenderer's own submit path, since `nested` (required here — see draftValues'
  // declaration comment above) suppresses FormRenderer's built-in Save/Cancel buttons.
  const handleFooterSave = useCallback(() => {
    void performSave(draftValues);
  }, [draftValues, performSave]);

  if (loading) {
    return (
      <div className="af-grid-modal__body af-grid-modal__body--loading">
        Loading form…
      </div>
    );
  }

  if (error || !schema) {
    return (
      <div className="af-grid-modal__body af-grid-modal__body--error">
        <p>{error ?? 'Unable to load form'}</p>
        <div className="af-grid-modal__footer">
          <button className="af-grid-modal__btn af-grid-modal__btn--secondary" onClick={onCancel}>
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="af-grid-modal__form-body">
      <FormRenderer
        schema={schema}
        initialValues={initialValues}
        mode={mode}
        onSubmit={handleSubmit}
        onValuesChange={setDraftValues}
        onClose={onCancel}
        submitLabel="Save"
        cancelLabel="Cancel"
        nested
      />
      {saveError && (
        <div className="af-grid-modal__body af-grid-modal__body--error" role="alert">
          {saveError}
        </div>
      )}
      <div className="af-grid-modal__footer">
        <button className="af-grid-modal__btn af-grid-modal__btn--secondary" onClick={onCancel} disabled={saving}>
          {mode === 'view' ? 'Close' : 'Cancel'}
        </button>
        {mode === 'edit' && (
          <button
            className="af-grid-modal__btn af-grid-modal__btn--primary"
            onClick={handleFooterSave}
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        )}
      </div>
    </div>
  );
};

// ─── Overlay modal shell ──────────────────────────────────────────────────────

export const GridEditModal: React.FC<GridEditModalProps> = ({
  row,
  columns,
  editFormID,
  formMode = 'edit',
  saveEndpoint,
  saveMethod,
  dataRootPath,
  onSave,
  onCancel,
}) => {
  // Close on Escape
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  const stopProp = (e: React.MouseEvent) => e.stopPropagation();

  const safeColumnsForTitle = columns ?? [];
  const firstLabelCol = safeColumnsForTitle.find(
    (c) => c.key && !c.key.startsWith('_') && c.type !== 'computed' && c.type !== 'selector' && c.type !== 'link',
  );
  const firstValue = firstLabelCol ? String(row[firstLabelCol.key] ?? '').trim() : '';
  const actionWord = formMode === 'view' ? 'View' : 'Edit';
  const rowTitle = editFormID != null && editFormID > 0
    ? (firstValue ? `${actionWord}: ${firstValue}` : actionWord)
    : firstValue
      ? `Edit: ${firstValue}`
      : firstLabelCol?.label
        ? `Edit ${firstLabelCol.label}`
        : 'Edit Record';

  return (
    <div className="af-grid-modal__overlay" onClick={onCancel} role="dialog" aria-modal="true">
      <div className="af-grid-modal__dialog" onClick={stopProp}>
        <div className="af-grid-modal__header">
          <span className="af-grid-modal__title">{rowTitle}</span>
          <button
            className="af-grid-modal__close"
            aria-label="Close"
            onClick={onCancel}
          >
            ×
          </button>
        </div>
        {editFormID != null && editFormID > 0 ? (
          <FormModalBody
            row={row}
            editFormID={editFormID}
            mode={formMode}
            saveEndpoint={saveEndpoint}
            saveMethod={saveMethod}
            dataRootPath={dataRootPath}
            onSave={onSave}
            onCancel={onCancel}
          />
        ) : (
          <BuiltinModalBody
            row={row}
            columns={columns}
            onSave={onSave}
            onCancel={onCancel}
          />
        )}
      </div>
    </div>
  );
};
